import { faMusic, faPlus, faTimes, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type React from "react";
import { useState, useRef, useEffect } from "react";
import { parseLyrics, parseTimedLyrics, type TimedLine } from "../utils/lyricsParser";

interface PreviewAreaProps {
  lyrics: string;
  mappings: Map<string, string>;
  spriteSize: number;
  hidePokeballs: boolean;
  showRed: boolean;
  caseInsensitive: boolean;
  removeMapping: (text: string) => void;
  addMapping: (text: string, pokemon: string) => void;
  selectedPokemon: string | null;
  setTextInput: (text: string) => void;
  timedLines?: TimedLine[];
  currentTime?: number;
  isPlaying?: boolean;
  onSeekToTime?: (time: number) => void;
  revealAllPokemon: boolean;
}

interface WordComponentProps {
  word: { type: string; text: string; pokemon?: string | null; lineIndex?: number };
  hidePokeballs: boolean;
  removeMapping: (text: string) => void;
  addMapping: (text: string, pokemon: string) => void;
  selectedPokemon: string | null;
  isHighlighted?: boolean;
  currentLineIndex: number;
  isSelected?: boolean;
  onToggleSelect?: (word: { type: string; text: string; pokemon?: string | null; lineIndex?: number }) => void;
  isPlaying?: boolean;
  isNextLine?: boolean;
  spriteSize: number;
  revealedPokemon: Set<string>;
  pokemonToReveal: Set<string>;
}

function PreviewArea({
  lyrics,
  mappings,
  spriteSize,
  hidePokeballs,
  showRed,
  caseInsensitive,
  removeMapping,
  addMapping,
  selectedPokemon,
  setTextInput,
  timedLines,
  currentTime = 0,
  isPlaying = false,
  onSeekToTime,
  revealAllPokemon,
}: PreviewAreaProps): React.JSX.Element {
  const [selectedWords, setSelectedWords] = useState<Set<string>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Determine which line should be highlighted
  const getCurrentLineIndex = (): number => {
    if (!timedLines || timedLines.length === 0) return -1;
    
    // Find the current line based on timing
    let currentLineIndex = -1;
    for (let i = 0; i < timedLines.length; i++) {
      if (currentTime >= timedLines[i].time) {
        currentLineIndex = i;
      } else {
        break;
      }
    }
    return currentLineIndex;
  };

  const currentLineIndex = getCurrentLineIndex();
  
  // Parse lyrics - use timed lyrics if available, otherwise fall back to regular parsing
  const words = timedLines && timedLines.length > 0 
    ? parseTimedLyrics(timedLines, mappings, caseInsensitive)
    : parseLyrics(lyrics, mappings, caseInsensitive);

  // Function to get Pokemon that should be revealed based on "One for All" mode
  const getRevealedPokemon = (): Set<string> => {
    const revealed = new Set<string>();
    
    // If revealAllPokemon ("One for All") is enabled, reveal Pokemon that have appeared in ANY played line
    if (revealAllPokemon) {
      if (!timedLines || timedLines.length === 0 || currentLineIndex < 0) return revealed;
      
      // Check all words in lines that have been played (currentLineIndex >= lineIndex)
      words.forEach(word => {
        if (word.lineIndex !== undefined && 
            word.lineIndex <= currentLineIndex && 
            word.pokemon && 
            word.pokemon !== "grass" && 
            word.pokemon !== "pokeflute") {
          revealed.add(word.pokemon);
        }
      });
    }
    
    return revealed;
  };

  // Function to get Pokemon that should show their sprites based on "Open Pokeballs" mode
  const getPokemonToReveal = (): Set<string> => {
    const toReveal = new Set<string>();
    
    // If hidePokeballs ("Open Pokeballs") is enabled, show all Pokemon sprites
    if (hidePokeballs) {
      words.forEach(word => {
        if (word.pokemon && 
            word.pokemon !== "grass" && 
            word.pokemon !== "pokeflute") {
          toReveal.add(word.pokemon);
        }
      });
    }
    
    return toReveal;
  };

  const revealedPokemon = getRevealedPokemon();
  const pokemonToReveal = getPokemonToReveal();

  // Function to scroll to a specific line
  const scrollToLine = (lineIndex: number): void => {
    if (!scrollContainerRef.current || lineIndex < 0) return;
    
    // Find the line element by its data-line-index attribute
    const lineElement = scrollContainerRef.current.querySelector(`[data-line-index="${lineIndex}"]`);
    if (lineElement) {
      const container = scrollContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const lineRect = lineElement.getBoundingClientRect();
      
      // Calculate the line's position relative to the container
      const lineTop = lineRect.top - containerRect.top + container.scrollTop;
      
      // Scroll to position the line slightly above center (about 1/3 from top)
      const targetScrollTop = lineTop - (containerRect.height * 0.3);
      
      // Smooth scroll to the target position
      container.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth'
      });
    }
  };

  // Auto-scroll to current line when currentTime changes
  useEffect(() => {
    if (currentLineIndex >= 0) {
      // Immediate scroll for smooth progression through rows
      scrollToLine(currentLineIndex);
    }
  }, [currentLineIndex]);


  const handleToggleSelect = (word: { type: string; text: string; pokemon?: string | null; lineIndex?: number }): void => {
    console.log('handleToggleSelect called with:', word);
    if (word.type === "newline" || (word.pokemon !== "grass" && word.pokemon !== null)) {
      console.log('Word not selectable:', { type: word.type, pokemon: word.pokemon });
      return; // Only allow selecting unmapped words
    }
    
    const wordKey = `${word.text}-${word.lineIndex}`;
    setSelectedWords(prev => {
      const newSet = new Set(prev);
      
      // If selecting a word from a different line, clear previous selections
      const currentLineIndex = word.lineIndex;
      const hasWordsFromOtherLines = Array.from(prev).some(key => {
        const [, lineIndexStr] = key.split('-');
        return parseInt(lineIndexStr, 10) !== currentLineIndex;
      });
      
      if (hasWordsFromOtherLines) {
        newSet.clear();
      }
      
      if (newSet.has(wordKey)) {
        newSet.delete(wordKey);
      } else {
        // Check if the new word would be consecutive with existing selections
        const words = timedLines && timedLines.length > 0 
          ? parseTimedLyrics(timedLines, mappings, caseInsensitive)
          : parseLyrics(lyrics, mappings, caseInsensitive);
        
        const currentWordIndex = words.findIndex(w => w.text === word.text && w.lineIndex === word.lineIndex);
        
        if (newSet.size > 0) {
          // Get indices of currently selected words in the same line
          const selectedIndices = Array.from(newSet)
            .map(key => {
              const [text, lineIndexStr] = key.split('-');
              const lineIndex = parseInt(lineIndexStr, 10);
              if (lineIndex === currentLineIndex) {
                return words.findIndex(w => w.text === text && w.lineIndex === lineIndex);
              }
              return -1;
            })
            .filter(index => index !== -1)
            .sort((a, b) => a - b);
          
          // Check if the new word is consecutive with existing selections
          const minIndex = Math.min(...selectedIndices);
          const maxIndex = Math.max(...selectedIndices);
          
          const isConsecutive = currentWordIndex === minIndex - 1 || 
                               currentWordIndex === maxIndex + 1 ||
                               (currentWordIndex >= minIndex && currentWordIndex <= maxIndex);
          
          if (!isConsecutive) {
            console.log('Word not consecutive, clearing previous selections');
            newSet.clear();
          }
        }
        
        newSet.add(wordKey);
      }
      
      // Update text input with selected words in left-to-right order
      const words = timedLines && timedLines.length > 0 
        ? parseTimedLyrics(timedLines, mappings, caseInsensitive)
        : parseLyrics(lyrics, mappings, caseInsensitive);
      
      const selectedWordObjects = Array.from(newSet).map(key => {
        const [text, lineIndexStr] = key.split('-');
        const lineIndex = parseInt(lineIndexStr, 10);
        return words.find(w => w.text === text && w.lineIndex === lineIndex);
      }).filter(Boolean);
      
      // Sort by line index and then by position within line
      selectedWordObjects.sort((a, b) => {
        if (a!.lineIndex !== b!.lineIndex) {
          return a!.lineIndex! - b!.lineIndex!;
        }
        // For same line, sort by their position in the original word array
        const aIndex = words.findIndex(w => w.text === a!.text && w.lineIndex === a!.lineIndex);
        const bIndex = words.findIndex(w => w.text === b!.text && w.lineIndex === b!.lineIndex);
        return aIndex - bIndex;
      });
      
      const textToAdd = selectedWordObjects.map(w => w!.text).join(' ');
      console.log('Adding text to input:', textToAdd);
      setTextInput(textToAdd);
      
      return newSet;
    });
  };

  if (!lyrics.trim()) {
    return (
      <section className="flex-1 flex flex-col bg-background-primary">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <div className="text-6xl text-secondary mb-4">
              <FontAwesomeIcon icon={faMusic} />
            </div>
            <h3 className="text-2xl font-semibold text-primary mb-2">Load lyrics and add mappings to see the preview!</h3>
            <p className="text-secondary">Paste your lyrics to the right, and start mapping words to Pokémon</p>
          </div>
        </div>
      </section>
    );
  }

  // Calculate which words belong to the current line for highlighting
  const getWordLineIndex = (wordIndex: number): number => {
    if (!timedLines || timedLines.length === 0) return -1;
    
    // If the word has a lineIndex, use it directly
    if (words[wordIndex] && typeof words[wordIndex].lineIndex === 'number') {
      return words[wordIndex].lineIndex!;
    }
    
    // Fallback to the old logic
    let lineIndex = 0;
    let wordCount = 0;
    
    for (let i = 0; i < words.length; i++) {
      if (words[i].type === "newline") {
        if (wordIndex <= i) {
          return lineIndex;
        }
        lineIndex++;
        wordCount = i + 1;
      }
    }
    
    return lineIndex;
  };

  return (
    <section className="flex-1 flex flex-col bg-background-primary">
      {selectedWords.size > 0 && (
        <div className="bg-background-secondary border-b border-border-primary px-4 py-2 flex items-center justify-between">
          <div className="text-primary font-medium">
            {selectedWords.size} word{selectedWords.size !== 1 ? 's' : ''} selected
          </div>
          <button
            className="btn-secondary"
            onClick={() => setSelectedWords(new Set())}
          >
            <FontAwesomeIcon icon={faXmark} />
            Clear Selection
          </button>
        </div>
      )}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-2 py-8 smooth-scroll">
        <div className="flex gap-1">
          {/* Lyrics organized by rows */}
          <div className="flex-1">
            {(() => {
              // Group words by line index to create rows
              const lineGroups = new Map<number, typeof words>();
              words.forEach(word => {
                if (word.lineIndex !== undefined) {
                  if (!lineGroups.has(word.lineIndex)) {
                    lineGroups.set(word.lineIndex, []);
                  }
                  lineGroups.get(word.lineIndex)!.push(word);
                }
              });

              const sortedLines = Array.from(lineGroups.entries()).sort((a, b) => a[0] - b[0]);

              return sortedLines.map(([lineIndex, lineWords], rowIndex) => {
                const isActive = currentLineIndex === lineIndex;
                const isPast = currentLineIndex > lineIndex;
                const lineText = lineWords.filter(w => w.type === 'word').map(w => w.text).join(' ');

                return (
                  <div key={lineIndex} data-line-index={lineIndex} className="flex items-center gap-1 mb-4 relative">
                    {/* Timeline node for this row */}
                    <div className="timeline-container">
                      {isActive ? (
                        showRed ? (
                          <div className={isPlaying ? "animate-red-walk" : "animate-red-walk-paused"} title={`Line ${rowIndex + 1}: ${lineText.substring(0, 30)}...`}></div>
                        ) : (
                          <div className={isPlaying ? "animate-leaf-walk" : "animate-leaf-walk-paused"} title={`Line ${rowIndex + 1}: ${lineText.substring(0, 30)}...`}></div>
                        )
                      ) : (
                        <button
                          type="button"
                          className="timeline-node"
                          title={`Line ${rowIndex + 1}: ${lineText.substring(0, 30)}...`}
                          onClick={() => {
                            if (timedLines && timedLines.length > lineIndex && onSeekToTime) {
                              const targetTime = timedLines[lineIndex].time;
                              console.log(`PreviewArea: Timeline node clicked - Line ${lineIndex + 1} at time ${targetTime.toFixed(2)}s`);
                              onSeekToTime(targetTime);
                            }
                          }}
                        >
                          <img
                            src={lineText.includes("♪") || lineText.includes("♫") ? "/icons/subnode.png" : "/icons/node.png"}
                            alt="Timeline node"
                            className="w-full h-full pixelated"
                          />
                        </button>
                      )}
                    </div>

                    {/* Words for this row */}
                    <div
                      className="flex flex-wrap gap-xs"
                      style={{ "--sprite-size": `${spriteSize}px` } as React.CSSProperties}
                    >
                      {lineWords.map((word, wordIndex) => {
                        const wordKey = `${word.text}-${word.lineIndex}`;
                        const isSelected = selectedWords.has(wordKey);
                        const isNextLine = currentLineIndex >= 0 && lineIndex === currentLineIndex + 1;

                        return (
                          <WordComponent
                            key={`${word.text}-${lineIndex}-${wordIndex}`}
                            word={word}
                            hidePokeballs={hidePokeballs}
                            removeMapping={removeMapping}
                            addMapping={addMapping}
                            selectedPokemon={selectedPokemon}
                            isHighlighted={isActive}
                            currentLineIndex={currentLineIndex}
                            isSelected={isSelected}
                            onToggleSelect={handleToggleSelect}
                            isPlaying={isPlaying}
                            isNextLine={isNextLine}
                            spriteSize={spriteSize}
                            revealedPokemon={revealedPokemon}
                            pokemonToReveal={pokemonToReveal}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>
    </section>
  );
}

function WordComponent({
  word,
  hidePokeballs,
  removeMapping,
  addMapping,
  selectedPokemon,
  isHighlighted = false,
  currentLineIndex,
  isSelected = false,
  onToggleSelect,
  isPlaying = false,
  isNextLine = false,
  spriteSize,
  revealedPokemon,
  pokemonToReveal,
}: WordComponentProps): React.JSX.Element {
  const shouldAnimate = isHighlighted && isPlaying;
  
  if (word.type === "newline") {
    return <div style={{ flexBasis: "100%", height: "0px" }} />;
  }

  const isMapped = word.pokemon && word.pokemon !== "grass";
  const hasLinePlayed = word.lineIndex !== undefined && currentLineIndex > word.lineIndex;
  const isCurrentlyPlaying = isHighlighted || (currentLineIndex >= 0 && word.lineIndex === currentLineIndex);
  const isPokemonRevealed = word.pokemon && revealedPokemon.has(word.pokemon);
  const shouldShowPokemonSprite = word.pokemon && pokemonToReveal.has(word.pokemon);
  
  let pokemonToUse: string;
  if (!isMapped) {
    pokemonToUse = "grass";
  } else if (word.pokemon === "pokeflute") {
    // Pokeflute should always show as pokeflute, never as pokeball
    pokemonToUse = "pokeflute";
  } else if (shouldShowPokemonSprite || isCurrentlyPlaying || hasLinePlayed || isPokemonRevealed) {
    pokemonToUse = word.pokemon!;
  } else {
    pokemonToUse = "pokeball";
  }
  
  console.log('WordComponent render:', { text: word.text, pokemon: word.pokemon, isMapped, pokemonToUse });
  
  const isGrass = pokemonToUse === "grass";
  const isPokeball = pokemonToUse === "pokeball";
  const isPokeflute = pokemonToUse === "pokeflute";
  

  const handleUnmap = (): void => {
    if (isMapped) {
      removeMapping(word.text);
    }
  };

  const handleSelect = (): void => {
    console.log('handleSelect called for word:', word.text);
    if (onToggleSelect) {
      onToggleSelect(word);
    }
  };

  const handleQuickMap = (): void => {
    if (selectedPokemon && !isMapped) {
      addMapping(word.text, selectedPokemon);
    }
  };

  const getWordClassName = () => {
    if (isHighlighted) {
      return `pokemon-word-active animate-bounce ${!isPlaying ? 'animate-bounce-paused' : ''}`;
    }
    if (isSelected) {
      return 'pokemon-word-selected';
    }
    return 'pokemon-word';
  };

  return (
    <span className={`group ${getWordClassName()}`}>
      <span className="flex flex-col items-center gap-1">
        <img
          src={
            isGrass || isPokeball || isPokeflute
              ? `/icons/${pokemonToUse}.png`
              : pokemonToUse.endsWith('-shiny')
                ? shouldAnimate
                  ? `/pokemon/animated/shiny/${pokemonToUse}.gif`
                  : `/pokemon/static/shiny/${pokemonToUse}.png`
                : shouldAnimate
                  ? `/pokemon/animated/base/${pokemonToUse}.gif`
                  : `/pokemon/static/base/${pokemonToUse}.png`
          }
          alt={word.text}
          loading="lazy"
          className={`pokemon-sprite ${
            isPokeball && isNextLine && isPlaying ? 'animate-pokeball-shake' : 
            isPokeball ? 'pokeball-scale' : ''
          }`}
          style={
            isGrass || isPokeball || isPokeflute
              ? { transform: "scale(0.75)", transformOrigin: "center" }
              : {}
          }
        />
        <span 
          className="pokemon-text"
          style={{ fontSize: `${Math.max(12, spriteSize * 0.28125)}px` }}
        >
          {word.text}
        </span>
      </span>
      {isMapped && (
        <button
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white border-none text-sm font-bold cursor-pointer flex items-center justify-center opacity-0 scale-80 transition-all z-10 shadow-md hover:bg-red-600 hover:scale-110 group-hover:opacity-100 group-hover:scale-100"
          onClick={handleUnmap}
          title="Remove mapping"
          type="button"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
      {!isMapped && (
        <button
          className={`absolute -top-2 -right-2 w-5 h-5 rounded-full bg-green-500 text-white border border-slate-600 cursor-pointer text-sm transition-all flex items-center justify-center hover:bg-green-600 hover:scale-110 z-10 bg-slate-900 ${
            isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-70 hover:opacity-100'
          }`}
          onClick={handleSelect}
          title="Select for bulk mapping"
          type="button"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      )}
    </span>
  );
}

export default PreviewArea;
